import bilAPI from "./api";

// The AUTHORED inputs the timetable generator reads:
//
//   /org/activities/           the curriculum — one line per class × subject
//   /org/rooms/                the contended rooms (labs, gym, music) only
//   /org/schedule-constraints/ the rules — school-wide defaults + overrides
//   /org/timetable-settings/   the caller school's week + bell schedule
//
// Runs themselves live in `timetableGenerationService.js`; the hand-authored /
// CSV-imported grid these eventually overwrite lives in `timetableService.js`.
// All three surfaces are gated by `organization.timetable.manage`.

// -- activities (the curriculum) ----------------------------------------------

// Paginated { count, next, previous, results }. Rows are READ-shaped: `group`
// and `subject` are nested { uuid, name, code }, `teachers` is a list of
// { uuid, first_name, last_name, email }, `term` is a uuid with `term_name`
// alongside, and `card_durations` is the derived card list (an empty `split`
// means every period is its own single-period card).
export const getActivities = ({
  params: {
    organization,
    term,
    group,
    subject,
    teacher,
    is_active,
    search,
    ordering,
    page,
    page_size,
  } = {},
  signal,
} = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .get(`/org/activities/`, {
          params: {
            organization,
            term,
            group,
            subject,
            teacher,
            is_active,
            search,
            ordering,
            page,
            page_size,
          },
          signal,
        })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

// body: { group, subject, teachers: [uuid], periods_per_week, split?, notes?,
//         term? } — `organization` is NEVER sent: the backend derives it from
// the chosen class. `term` defaults to the term covering today.
export const createActivity = ({ body, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .post(`/org/activities/`, body, { signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

export const updateActivity = ({ uuid, body, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .patch(`/org/activities/${uuid}/`, body, { signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

export const deleteActivity = ({ uuid, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .delete(`/org/activities/${uuid}/`, { signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

// -- rooms (only the CONTENDED ones) ------------------------------------------

// A school lists only the rooms lessons actually compete for — labs, the gym,
// the music room. A class that stays in its own room can never clash with
// itself, so those rooms are deliberately NOT modelled: a lesson whose room
// pool resolves empty is simply scheduled without a room.
//
// Rows: { uuid, organization, name, code, building, capacity, is_shared,
//         subjects: [uuid], is_active }. Because the list is meant to stay
// short, callers fetch it in one page rather than paginating it.
export const getRooms = ({
  params: {
    organization,
    is_shared,
    subject,
    is_active,
    search,
    ordering,
    page,
    page_size,
  } = {},
  signal,
} = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .get(`/org/rooms/`, {
          params: {
            organization,
            is_shared,
            subject,
            is_active,
            search,
            ordering,
            page,
            page_size,
          },
          signal,
        })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

// body: { name, code?, building?, capacity?, is_shared?, subjects?: [uuid] } —
// `organization` is never sent: rooms belong to the caller's own school.
export const createRoom = ({ body, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .post(`/org/rooms/`, body, { signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

export const updateRoom = ({ uuid, body, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .patch(`/org/rooms/${uuid}/`, body, { signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

export const deleteRoom = ({ uuid, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .delete(`/org/rooms/${uuid}/`, { signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

// -- schedule constraints (the authored rules) --------------------------------

// Paginated. `?target=none` isolates the school-wide defaults; `?target=teacher`
// (or group/subject/activity) isolates the per-resource overrides.
export const getScheduleConstraints = ({
  params: {
    organization,
    kind,
    target,
    teacher,
    group,
    subject,
    activity,
    is_active,
    ordering,
    page,
    page_size,
  } = {},
  signal,
} = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .get(`/org/schedule-constraints/`, {
          params: {
            organization,
            kind,
            target,
            teacher,
            group,
            subject,
            activity,
            is_active,
            ordering,
            page,
            page_size,
          },
          signal,
        })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

// body: { kind, weight?, params?, and AT MOST ONE of teacher/group/subject/
//         activity }. All four omitted/null = the school-wide default for that
// kind. weight 100 = hard, anything lower = soft.
export const createScheduleConstraint = ({ body, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .post(`/org/schedule-constraints/`, body, { signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

export const updateScheduleConstraint = ({ uuid, body, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .patch(`/org/schedule-constraints/${uuid}/`, body, { signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

export const deleteScheduleConstraint = ({ uuid, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .delete(`/org/schedule-constraints/${uuid}/`, { signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

// The constraint-kind VOCABULARY. Resolves a plain array (not paginated) of
// { kind, label, allowed_targets: [...], params_schema: <jsonschema> }.
// The rule builder must render from this — never from a hardcoded kind list, or
// it silently drops every kind added on the backend afterwards.
export const getConstraintKinds = ({ signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .get(`/org/schedule-constraints/kinds/`, { signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

// -- timetable settings (self-scoped singleton) -------------------------------

// The CALLER school's week and bell schedule — no ?organization= drill-down,
// same shape as the rubric activation / material adaptation endpoints. The row
// is materialised on first touch, so a GET always resolves.
// { uuid, organization, teaching_weekdays: [0..6], periods: [{period,start,end}],
//   periods_per_day }
// That is the whole payload: the days a school teaches and the bells that
// divide them. Three things have been removed from it rather than left to imply
// a capability that did not exist —
//   `default_time_limit_seconds` (organization/0018) — an unanswerable "guess a
//      number" question, and an availability bug in the stale-run reclaim rule;
//   `enforce_qualification`      (organization/0020) — read by nothing, and
//      unimplementable at that layer: the solver never assigns teachers, it
//      places lessons whose teachers were fixed on the Lessons tab;
//   `notes`                      (organization/0021) — written and read only by
//      the textarea that edited it, so a handover note reached nobody.
export const getTimetableSettings = ({ signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .get(`/org/timetable-settings/`, { signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

// PUT patches the singleton (partial=True server-side): send only what changed.
// `teaching_weekdays` is JS getDay() order (0 = Sunday … 6 = Saturday) and comes
// back sorted; `periods` must be listed in order and numbered contiguously from
// 1, each with HH:MM `start` < `end`. `periods_per_day` is derived, never sent.
export const updateTimetableSettings = ({ body, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .put(`/org/timetable-settings/`, body, { signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

// -- academic terms (ONE school's calendar) -----------------------------------

// Reference data for the term-scoped surfaces. `Activity` is unique per
// (term, group, subject) and the generator solves exactly ONE term, so any
// screen that lists curriculum or starts a run has to be able to name them.
// Unpaginated (a flat array); each row carries `is_current`.
//
// The list is ONE COHERENT CALENDAR, already resolved by the backend: a school
// with terms of its own gets only those, a school with none gets the network
// defaults (`organization: null`, flagged `is_network_default`). Never both —
// a school's own term_1 is expected to sit on top of the default it replaces,
// so merging the two sets would publish overlapping windows and `for_date`
// would answer with whichever row it scanned first.
//
// `organization` picks whose calendar to read; omitting it means the caller's
// own school, resolved from their staff link. Prefer omitting it: the server
// picks the caller's PRIMARY active link by `created_at`, while `/auth/me/`
// orders `organizations` by `-is_in_progress, -start_date`, so a manager
// staffed at two schools can have a different "first" school at each end.
export const getAcademicTerms = ({ params: { organization } = {}, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .get(`/core/academic-terms/`, { params: { organization }, signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

// body: { name, date_from: "MM-DD", date_till: "MM-DD" } — `organization` is
// deliberately NOT sent, the same rule the rooms and lessons writes follow: the
// backend files the term against the caller's own school. Sending it explicitly
// is how a multi-school manager targets another of their schools, and an
// explicit null asks for a NETWORK DEFAULT, which only a superuser may file.
//
// Writing the FIRST term takes the school off the network defaults entirely, so
// a caller that means to keep the current calendar has to copy all of it.
//
// Refusals worth handling by name: 403 `detail` when the row is a network
// default or another school's; 400 `{date_from|date_till: [...]}` from the
// model's own `clean()` (overlap with a sibling term, an inverted range, an
// MM-DD that is not a real date) — those belong beside the field, not in a toast.
export const createAcademicTerm = ({ body, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .post(`/core/academic-terms/`, body, { signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

// `organization` is WRITE-ONCE: accepted on create, refused here. A term does
// not move between schools — Activity, TimetableRun and Observation all PROTECT
// it, so relocating the row would leave the old school's curriculum and its
// filed observations pointing at a calendar that school no longer follows.
export const updateAcademicTerm = ({ uuid, body, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .patch(`/core/academic-terms/${uuid}/`, body, { signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

// Deleting a school's LAST own term returns it to the network defaults. A term
// still held by a lesson, a generation run or an observation is a 400 carrying
// one sentence (a bare array), not a 500 — those three FKs are PROTECT.
export const deleteAcademicTerm = ({ uuid, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .delete(`/core/academic-terms/${uuid}/`, { signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};
